import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_COMMUNITIES_QUERY_KEY } from './useGetCommunities';
import { QUERY_KEY as GET_PUBLIC_COMMUNITIES_QUERY_KEY } from './useGetPublicCommunities';

export type ApiRequest_Join_Community = {
  communityId: number;
};

export type ApiResponse_Join_Community = {
  id: number;
  community_id: number;
  user_id: string;
  role: string;
  is_verified: boolean;
  joined_at: string;
  status: string;
};

const METHOD = 'POST';
const ENDPOINT = 'communities/{communityId}/join';

export const useJoinCommunity = (options?: {
  onSuccess?: (data: ApiResponse_Join_Community) => void;
  onError?: (error: Error) => void;
}) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Join_Community) => {
      try {
        const data = await handleApiCall<
          ApiResponse_Join_Community,
          Record<string, never>
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { communityId: params.communityId.toString() }),
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to join community.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data) => {
      // Show different message based on membership status
      if (data.status === 'PENDING') {
        enqueueSnackbar('Join request sent! Waiting for admin approval.', { variant: 'info' });
      } else {
        enqueueSnackbar('Successfully joined community!', { variant: 'success' });
      }
      
      // Invalidate relevant queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: GET_COMMUNITIES_QUERY_KEY() });
      queryClient.invalidateQueries({ queryKey: GET_PUBLIC_COMMUNITIES_QUERY_KEY() });
      
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};